import React, { useCallback, useMemo, createContext, useState } from 'react';

import { AnyRecord } from '../utils';
import createContextHook from '../utils/createContextHook';

import {
    TableFiltersProviderProps as Props,
    TableFiltersProviderContextValue as CtxValue,
} from './TableFiltersProvider.types';

const TableFiltersContext = createContext<CtxValue>(null as unknown as CtxValue);

TableFiltersContext.displayName = 'TableFiltersContext';

const useTableFilters = createContextHook(TableFiltersContext);

function validateValue(value: any) {
    if (!Array.isArray(value) || value.length) {
        return value;
    }

    return undefined;
}

function TableFiltersProvider({ initialValues, children }: Props) {
    const [values, setValues] = useState(initialValues);
    const [isOpened, setIsOpened] = useState(false);

    const open = useCallback(() => setIsOpened(true), []);

    const clear = useCallback(() => {
        setValues(initialValues);
    }, [initialValues]);

    const close = useCallback(() => {
        setIsOpened(false);
    }, []);

    const change = useCallback(
        (field: string) => (value: any) =>
            setValues((currentValues: AnyRecord) => ({
                ...currentValues,
                [field]: validateValue(value),
            })),
        [],
    );

    const value: CtxValue = useMemo(
        () => ({
            isOpened,
            open,
            close,
            values,
            initialValues,
            change,
            clear,
            isApplied: !isOpened && Object.values(values).some(v => v),
        }),
        [isOpened, open, close, change, values, initialValues, clear],
    );

    if (!initialValues) {
        throw new Error('Provide `initialValues` in TableFiltersProvider');
    }

    return <TableFiltersContext.Provider value={value}>{children}</TableFiltersContext.Provider>;
}

export { useTableFilters, TableFiltersProvider };
